import {
  RUTERO_BOOKING_LIST_LIMITS,
  RuteroBookingListSort,
  RuteroBookingListSortDirection,
  RuteroBookingListTab,
  BookingListVertical,
} from '@bandido/shared/types';

import { BookingState } from './bookingState';
import { bookingListTabPaginates, bookingListTabSupportsSort } from './bookingList';

/**
 * Los filtros del listado y su traducción EXACTA a la query del endpoint.
 *
 * Todos son opcionales y componibles entre sí y con la pestaña, igual que en
 * el contrato (`ListRuteroBookingsDto`). Los topes son los del DTO —importados,
 * no copiados—: un texto más largo que lo que el borde acepta se corta en el
 * campo (que declara el mismo `maxLength`), nunca en silencio al enviarlo
 * (§21.22.5).
 */
export interface BookingListFilters {
  /** Filtro exacto que llega desde la ficha del caso. */
  caseId?: number;
  vertical?: BookingListVertical;
  provider?: string;
  state?: BookingState;
  /** Cuándo se presta el servicio, no cuándo se vendió. */
  serviceDateFrom?: string;
  serviceDateTo?: string;
  /** Reservas sin item de venta: plata sin contabilizar. */
  saleOrderless?: boolean;
  /** Texto libre sobre reserva, titular, número/asunto/cliente del caso. */
  q?: string;
}

export const EMPTY_BOOKING_LIST_FILTERS: BookingListFilters = {};

/** Contexto exacto de un deep link desde Caso; valores rotos no filtran. */
export function bookingCaseFilterFromSearch(
  search: URLSearchParams,
): BookingListFilters {
  const caseId = Number(search.get('caseId'));
  return Number.isInteger(caseId) && caseId > 0 && caseId <= 2_147_483_647
    ? { caseId }
    : {};
}

/**
 * Los estados que se pueden filtrar, tipados contra la unión que la pantalla
 * ya usa para leer una reserva: un estado inventado no compila. En el orden en
 * que se los busca — lo vivo primero, el historial al final.
 */
export const BOOKING_LIST_STATE_FILTERS: readonly BookingState[] = [
  'held',
  'confirmed',
  'quoted',
  'draft',
  'cancelled',
  'failed',
];

export interface BookingListSortState {
  sort: RuteroBookingListSort;
  dir: RuteroBookingListSortDirection;
}

const TEXT_FILTERS = ['provider', 'q'] as const;

function cleanText(value: string | undefined, max: number): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, max) : undefined;
}

/**
 * Cuántos filtros hay puestos: es lo único visible del panel en mobile, así
 * que tiene que contar exactamente lo que el panel puede limpiar.
 */
export function activeBookingListFilterCount(
  filters: BookingListFilters,
): number {
  return (
    (filters.vertical ? 1 : 0) +
    (filters.state ? 1 : 0) +
    (filters.saleOrderless ? 1 : 0) +
    (filters.caseId ? 1 : 0) +
    (filters.serviceDateFrom ? 1 : 0) +
    (filters.serviceDateTo ? 1 : 0) +
    TEXT_FILTERS.filter((key) => Boolean(filters[key]?.trim())).length
  );
}

export function hasActiveBookingListFilters(
  filters: BookingListFilters,
): boolean {
  return activeBookingListFilterCount(filters) > 0;
}

/**
 * La query del pedido, en UNA definición: es la que viaja al endpoint y la que
 * entra en la query key de React Query. Con dos derivaciones parecidas, la
 * cache podría servir la respuesta de otro ámbito o de otra pestaña — que es
 * justo lo que la key existe para evitar (§21.2).
 *
 * Nada vacío viaja (un filtro sin valor no filtra), `dir` NUNCA sale sin
 * `sort` (el borde lo rechaza con un 400 en vez de ignorarlo en silencio) y el
 * orden pedido solo se manda donde la pestaña lo ofrece: en "atención" y
 * "próximas" el orden ES el trabajo.
 */
export function bookingListQueryParams({
  tab,
  createdBy,
  filters,
  sort,
  page,
}: {
  tab: RuteroBookingListTab;
  createdBy?: string;
  filters: BookingListFilters;
  sort?: BookingListSortState;
  page: number;
}): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = { tab };
  if (createdBy) params.createdBy = createdBy;
  if (filters.vertical) params.vertical = filters.vertical;
  if (filters.state) params.state = filters.state;
  if (filters.saleOrderless) params.saleOrderless = true;
  if (filters.caseId) params.caseId = filters.caseId;
  if (filters.serviceDateFrom) params.serviceDateFrom = filters.serviceDateFrom;
  if (filters.serviceDateTo) params.serviceDateTo = filters.serviceDateTo;

  const provider = cleanText(
    filters.provider,
    RUTERO_BOOKING_LIST_LIMITS.providerMaxLength,
  );
  if (provider) params.provider = provider;
  const q = cleanText(filters.q, RUTERO_BOOKING_LIST_LIMITS.queryMaxLength);
  if (q) params.q = q;

  if (sort && bookingListTabSupportsSort(tab)) {
    params.sort = sort.sort;
    params.dir = sort.dir;
  }
  if (bookingListTabPaginates(tab) && page > 1) params.page = page;
  return params;
}

/**
 * El rango de fecha de servicio, siempre VÁLIDO: el borde rechaza un fin
 * anterior a su inicio con un 400 (y hace bien: un rango invertido no es un
 * filtro que se pueda ignorar en silencio). Elegir una punta que invalida la
 * otra la suelta, así que la pantalla nunca puede pedir lo que el contrato no
 * acepta.
 */
export function applyBookingListServiceDateRange(
  filters: BookingListFilters,
  part: 'serviceDateFrom' | 'serviceDateTo',
  value: string | undefined,
): BookingListFilters {
  const next: BookingListFilters = { ...filters };
  if (value) next[part] = value;
  else delete next[part];

  const { serviceDateFrom: from, serviceDateTo: to } = next;
  // `YYYY-MM-DD` ordena como texto igual que el calendario (mismo criterio que
  // la validacion del DTO).
  if (from && to && to < from) {
    delete next[part === 'serviceDateFrom' ? 'serviceDateTo' : 'serviceDateFrom'];
  }
  return next;
}

/**
 * Los proveedores salen de las facetas de reservas persistidas del ámbito,
 * incluyendo históricos aunque su conector ya no esté activo; se muestran
 * acotados al vertical elegido cuando hay uno: ofrecer "Delorean" mientras se
 * miran ferries es ofrecer un filtro que siempre da vacío.
 */
export function bookingListProviderOptions(
  facets: { provider: string; vertical: string }[] | undefined,
  vertical?: BookingListVertical,
): string[] {
  return uniqueProviders(facets, vertical);
}

/**
 * Las opciones que el select OFRECE, que siempre incluyen la que está puesta.
 * Las facetas pueden no haber cargado todavía, o el proveedor pudo dejar de
 * publicarse: sin esto el select mostraría "todos" mientras el filtro sigue
 * viajando — un filtro aplicado que la pantalla dice que no está (§21.9).
 */
export function bookingListProviderChoices(
  facets: { provider: string; vertical: string }[] | undefined,
  filters: BookingListFilters,
): string[] {
  const catalog = uniqueProviders(facets, filters.vertical);
  return filters.provider && !catalog.includes(filters.provider)
    ? [filters.provider, ...catalog]
    : catalog;
}

/**
 * Cambiar de vertical no puede dejar puesto un proveedor que ese vertical no
 * vende: el filtro seguiría viajando, la lista daría vacío y el chip que lo
 * explica ya no estaría entre las opciones. Se suelta el proveedor y se avisa
 * con lo único honesto — que deje de estar puesto.
 */
export function applyBookingListVerticalFilter(
  filters: BookingListFilters,
  vertical: BookingListVertical | undefined,
  facets: { provider: string; vertical: string }[] | undefined,
): BookingListFilters {
  const next: BookingListFilters = { ...filters };
  if (vertical) next.vertical = vertical;
  else delete next.vertical;
  // Sin facetas cargadas no se sabe qué vende el vertical nuevo: se conserva
  // lo elegido en vez de soltarlo por no saber.
  if (
    next.provider &&
    facets &&
    !uniqueProviders(facets, vertical).includes(next.provider)
  ) {
    delete next.provider;
  }
  return next;
}

function uniqueProviders(
  facets: { provider: string; vertical: string }[] | undefined,
  vertical?: BookingListVertical,
): string[] {
  const providers = (facets ?? [])
    .filter((facet) => !vertical || facet.vertical === vertical)
    .map((facet) => facet.provider)
    .filter(Boolean);
  return [...new Set(providers)].sort((a, b) => a.localeCompare(b));
}
